import { AddCircle, RemoveCircle } from "@mui/icons-material";
import {
  Box,
  Button,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";

import CustomDatePicker from "../CustomDatePicker";
import CustomDialog from "../CustomDialog";

type EditPointMode = "increase" | "decrease";
type EditPointsSubmitData = {
  mode: EditPointMode;
  points: number;
  reason: string;
  expirationDate?: string;
  sendNotification: boolean;
};

type EditPointsDialogProps = {
  open: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onSubmit(data: EditPointsSubmitData): Promise<void>;
};

const EditPointsDialog = ({
  open,
  onClose,
  onSubmit,
}: EditPointsDialogProps) => {
  const [mode, setMode] = useState<EditPointMode>("increase");
  const [pointsValue, setPointsValue] = useState("");
  const [details, setDetails] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [notification, setNotification] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
    setMode("increase");
    setPointsValue("");
    setDetails("");
    setExpirationDate("");
    setNotification(true);
  };

  const handleSubmit = async () => {
    const points = Number(pointsValue);
    if (!Number.isFinite(points) || points <= 0) return;
    const trimmedReason = details.trim();
    if (!trimmedReason) return;
    if (mode === "increase" && !expirationDate) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        mode,
        points,
        reason: trimmedReason,
        expirationDate: mode === "increase" ? expirationDate : undefined,
        sendNotification: notification,
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled =
    isSubmitting ||
    !details.trim() ||
    !pointsValue ||
    Number(pointsValue) <= 0 ||
    (mode === "increase" && !expirationDate);

  const currentDateLabel = dayjs().format("DD MMMM YYYY");
  const detailsTitle =
    mode === "increase" ? "Details Increase Points" : "Details Decrease Points";
  const detailsPlaceholder =
    mode === "increase"
      ? "Enter Points Details Increase Points"
      : "Enter Points Details Decrease Points";
  const infoTitle =
    mode === "increase" ? "Increase Points Info" : "Decrease Points Info";
  const pointsPlaceholder =
    mode === "increase" ? "Enter Points" : "Enter Decrease Points";

  const handleExpirationDateChange = (value: Dayjs | null) => {
    setExpirationDate(value ? value.format("YYYY-MM-DD") : "");
  };

  const tabItems = [
    {
      value: "increase" as const,
      label: "Increase Points",
      icon: AddCircle,
    },
    {
      value: "decrease" as const,
      label: "Decrease Points",
      icon: RemoveCircle,
    },
  ];

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 600,
            lineHeight: 1.4,
            color: "grey.950",
          }}
        >
          Edit Points
        </Typography>
      }
      maxWidth="sm"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "542px",
          borderRadius: "4px",
          boxShadow:
            "0px 1px 3px 0px rgba(0,0,0,0.12), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.2)",
          "& .MuiDialogTitle-root": {
            px: 2,
            py: 2,
          },
          "& .MuiDialogContent-root": {
            px: 0,
            py: 0,
          },
        },
      }}
      content={
        <Box>
          <Stack
            direction="row"
            sx={{ borderBottom: "1px solid", borderColor: "primary.300" }}
          >
            {tabItems.map((item) => {
              const active = mode === item.value;
              const Icon = item.icon;

              return (
                <Button
                  key={item.value}
                  fullWidth
                  disableRipple
                  onClick={() => setMode(item.value)}
                  startIcon={
                    <Icon
                      sx={{
                        fontSize: 24,
                        color: active ? "primary.main" : "grey.400",
                      }}
                    />
                  }
                  sx={{
                    position: "relative",
                    minHeight: 57,
                    gap: 1,
                    px: 4,
                    py: 2,
                    borderRadius: 0,
                    textTransform: "none",
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: 1.4,
                    color: active ? "primary.main" : "grey.400",
                    "&::after": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 48,
                          right: 48,
                          bottom: 0,
                          height: "3px",
                          borderRadius: "2px",
                          bgcolor: "primary.main",
                        }
                      : {},
                    "&:hover": {
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>

          <Stack spacing={3} sx={{ px: 2, pt: 2, pb: 2 }}>
            <Stack spacing={2}>
              <Typography
                sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
              >
                {infoTitle}
              </Typography>

              <Box>
                <Typography sx={{ fontSize: 16, lineHeight: 1.4, mb: 1 }}>
                  Points
                  <Box component="span" sx={{ color: "error.dark" }}>
                    *
                  </Box>
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={pointsValue}
                  onChange={(event) => setPointsValue(event.target.value)}
                  placeholder={pointsPlaceholder}
                  inputProps={{ min: 1 }}
                  helperText="Please enter a valid number of points."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                    },
                    "& .MuiOutlinedInput-input": {
                      px: 1.5,
                      py: "16px",
                      fontSize: 16,
                      lineHeight: 1.4,
                    },
                    "& .MuiFormHelperText-root": {
                      mt: 0.375,
                      fontSize: 14,
                      lineHeight: 1.4,
                      color: "grey.400",
                    },
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 16, lineHeight: 1.4, mb: 1 }}>
                  {detailsTitle}
                  <Box component="span" sx={{ color: "error.dark" }}>
                    *
                  </Box>
                </Typography>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    multiline
                    minRows={3}
                    fullWidth
                    value={details}
                    onChange={(event) =>
                      setDetails(event.target.value.slice(0, 100))
                    }
                    placeholder={detailsPlaceholder}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        alignItems: "flex-start",
                        borderRadius: "4px",
                        pr: 1.5,
                        pb: 3.5,
                      },
                      "& .MuiOutlinedInput-input": {
                        px: 1.5,
                        pt: "16px",
                        pb: 0,
                        fontSize: 16,
                        lineHeight: 1.4,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      position: "absolute",
                      right: 12,
                      bottom: 8,
                      fontSize: 14,
                      lineHeight: 1.4,
                      color: "grey.400",
                      pointerEvents: "none",
                    }}
                  >
                    {details.length}/100
                  </Typography>
                </Box>
              </Box>
            </Stack>

            {mode === "increase" && (
              <Stack spacing={2}>
                <Typography
                  sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
                >
                  Start Date & Expiration Date
                </Typography>
                <Typography
                  sx={{ fontSize: 14, lineHeight: 1.4, color: "grey.400" }}
                >
                  Start Date is set to today{" "}
                  <Box
                    component="span"
                    sx={{ color: "primary.main", fontWeight: 400 }}
                  >
                    ({currentDateLabel})
                  </Box>
                  <br />
                  when this point campaign becomes effective.
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: 16, lineHeight: 1.4, mb: 1 }}>
                    Expiration Date
                    <Box component="span" sx={{ color: "error.dark" }}>
                      *
                    </Box>
                  </Typography>
                  <CustomDatePicker
                    fullWidth
                    disablePast
                    format="DD MMMM YYYY"
                    value={expirationDate ? dayjs(expirationDate) : null}
                    onChange={handleExpirationDateChange}
                    slotProps={{
                      textField: {
                        placeholder: "Select Expiration Date",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "4px",
                          },
                          "& .MuiOutlinedInput-input": {
                            px: 1.5,
                            py: "16px",
                            fontSize: 16,
                            lineHeight: 1.4,
                          },
                          "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            color: "grey.600",
                            fontSize: 24,
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Stack>
            )}

            <Stack spacing={1}>
              <Typography sx={{ fontSize: 16, lineHeight: 1.4 }}>
                Message Options
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={notification}
                  onChange={(event) => setNotification(event.target.checked)}
                />
                <Typography
                  sx={{ fontSize: 16, lineHeight: 1.4, color: "primary.main" }}
                >
                  Notifications are sent via LINE
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="outlined"
                disabled={isSubmitting}
                onClick={handleClose}
                sx={{
                  minWidth: 102,
                  height: 40,
                  px: 3,
                  borderRadius: "96px",
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.5,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "transparent",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={isDisabled}
                onClick={handleSubmit}
                sx={{
                  minWidth: 102,
                  height: 40,
                  px: 3,
                  borderRadius: "96px",
                  bgcolor: "primary.main",
                  color: "white.main",
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.5,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "primary.main",
                    boxShadow: "none",
                  },
                }}
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        </Box>
      }
    />
  );
};

export default EditPointsDialog;
